import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { PaylineForm } from "./PaylineForm";
import { ClickToPayForm } from "./ClickToPayForm";
import { YavinForm } from "./YavinForm";
import { ClickToPayConfig, PaylineConfig, YavinConfig } from "./types";

type ConfigModalProps = {
    isOpen: boolean;
    onClose: () => void;
    provider: "payline" | "clicktopay" | "yavin" | null;
    paylineConfig: PaylineConfig;
    clickToPayConfig: ClickToPayConfig;
    yavinConfig: YavinConfig;
    onPaylineSave: (config: PaylineConfig) => void;
    onClickToPaySave: (config: ClickToPayConfig) => void;
    onYavinSave: (config: YavinConfig) => void;
};

const TITLE_KEYS: Record<NonNullable<ConfigModalProps["provider"]>, string> = {
    payline: "Payline configuration",
    clicktopay: "Click to Pay configuration",
    yavin: "Yavin configuration",
};

export function ConfigModal({
    isOpen,
    onClose,
    provider,
    paylineConfig,
    clickToPayConfig,
    yavinConfig,
    onPaylineSave,
    onClickToPaySave,
    onYavinSave,
}: ConfigModalProps) {
    const { t } = useTranslation();

    // Draft state — only committed on Validate, discarded on Cancel/X
    const [draftPayline, setDraftPayline] = useState<PaylineConfig>(
        paylineConfig
    );
    const [draftClickToPay, setDraftClickToPay] = useState<ClickToPayConfig>(
        clickToPayConfig
    );
    const [draftYavin, setDraftYavin] = useState<YavinConfig>(yavinConfig);

    // Reset draft to current saved value whenever the modal opens
    useEffect(() => {
        if (isOpen) {
            setDraftPayline(paylineConfig);
            setDraftClickToPay(clickToPayConfig);
            setDraftYavin(yavinConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleValidate = () => {
        switch (provider) {
            case "payline":
                onPaylineSave(draftPayline);
                break;
            case "clicktopay":
                onClickToPaySave(draftClickToPay);
                break;
            case "yavin":
                onYavinSave(draftYavin);
                break;
        }
        onClose();
    };

    const handleCancel = () => {
        // Discard draft — original values are preserved in parent state
        onClose();
    };

    return (
        <Modal
            data-testid="payment-config-modal"
            isOpen={isOpen}
            centered
            backdrop="static"
            fade={false}
            toggle={handleCancel}
            className="payment-config-modal__clz"
        >
            <ModalHeader toggle={handleCancel} className="text-uppercase">
                {provider ? t(TITLE_KEYS[provider]) : ""}
            </ModalHeader>
            <ModalBody>
                <h6
                    data-testid="payment-config-instructions"
                    className="payment-config-instructions__clz"
                >
                    {t("Please fill in the fields")}
                </h6>
                <div
                    data-testid="online-payment-config"
                    style={{ padding: "15px 30px" }}
                >
                    {provider === "payline" && (
                        <PaylineForm
                            config={draftPayline}
                            onChange={(update) =>
                                setDraftPayline((prev) => ({
                                    ...prev,
                                    ...update,
                                }))
                            }
                        />
                    )}
                    {provider === "clicktopay" && (
                        <ClickToPayForm
                            config={draftClickToPay}
                            onChange={(update) =>
                                setDraftClickToPay((prev) => ({
                                    ...prev,
                                    ...update,
                                }))
                            }
                        />
                    )}
                    {provider === "yavin" && (
                        <YavinForm
                            config={draftYavin}
                            onChange={(update) =>
                                setDraftYavin((prev) => ({
                                    ...prev,
                                    ...update,
                                }))
                            }
                        />
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <CustomSecondaryColorButton
                    data-testid="payment-config-cancel"
                    outline
                    rounded
                    onClick={handleCancel}
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    data-testid="payment-config-validate"
                    rounded
                    onClick={handleValidate}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </Modal>
    );
}
